import { useMemo, useState } from 'react'
import { ResolutionRules } from './ResolutionRules'
import { countPreferRules } from '../resolveSpec'

/**
 * What to do about rows that share a key. The key itself is usually one column
 * (email); several means a row must match on all of them.
 */
export const DUP_MODES = [
  {
    id: 'duplicates',
    label: 'Show duplicates',
    verb: 'Showing rows that share',
    blurb: 'Only rows that repeat, grouped together, with the copy your rules keep marked.',
    scope: 'duplicate rows',
    exportLabel: 'Export duplicates',
    exportHint: 'Duplicate rows, with group numbers and which copy is kept',
    empty: 'No duplicates found.',
  },
  {
    id: 'dedupe',
    label: 'Hide duplicates',
    verb: 'Hiding repeats of',
    blurb: 'Every record once — the copy your rules keep, plus every row that never repeats.',
    scope: 'deduplicated rows',
    exportLabel: 'Export deduplicated',
    exportHint: 'The records with duplicates resolved — same columns as the file',
    empty: 'No rows match these filters.',
  },
  {
    id: 'kept',
    label: 'Kept copies',
    action: 'Show kept copies',
    verb: 'Keeping the winning copy of',
    blurb: 'Only the surviving copy of each repeated key — without the rows that never repeated.',
    scope: 'kept copies',
    exportLabel: 'Export kept copies',
    exportHint: 'The surviving copy of each repeated key — same columns as the file',
    empty: 'Nothing repeats here, so there is no copy to keep.',
  },
  {
    id: 'dropped',
    label: 'Removed rows',
    // The radio reads as a noun, the button has to read as a verb.
    action: 'Show removed',
    verb: 'Showing what deduplicating removes from',
    blurb: 'The other side of it: only the copies that get dropped, and what each lost to.',
    scope: 'removed rows',
    exportLabel: 'Export removed',
    exportHint: 'The copies deduplicating drops, each with the row it lost to',
    empty: 'Nothing to remove — no key repeats here.',
  },
  {
    id: 'unique',
    label: 'Only unique',
    verb: 'Keeping only rows unique on',
    blurb: 'Only rows whose key never repeats. Every copy of a repeated key is dropped.',
    scope: 'unique rows',
    exportLabel: 'Export unique',
    exportHint: 'Only rows whose key never repeats — same columns as the file',
    empty: 'No unique rows — every row here shares its key with another.',
  },
]

export const dupMode = (id) => DUP_MODES.find((m) => m.id === id) ?? DUP_MODES[0]

/** Modes where it matters *which* copy of a group wins, so the rules are shown. */
const RESOLVED_MODES = new Set(['duplicates', 'dedupe', 'kept', 'dropped'])

export function DuplicatePanel({
  columns,
  draft,
  setDraft,
  mode,
  setMode,
  resolution,
  setResolution,
  resolutionSpec,
  preferences,
  tiebreaks,
  maxResolveRules,
  applied,
  onFind,
  onExit,
  scanning,
}) {
  const [search, setSearch] = useState('')
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? columns.filter((c) => c.name.toLowerCase().includes(q)) : columns
  }, [columns, search])

  const chosen = [...draft]
  const active = applied !== null
  // The rules only count as a change for the modes that read them, so switching
  // to "only unique" and back does not look dirty.
  const resolves = RESOLVED_MODES.has(mode)
  const unchanged =
    active &&
    applied.mode === mode &&
    JSON.stringify(applied.columns) === JSON.stringify(chosen) &&
    (!resolves || JSON.stringify(applied.resolution ?? null) === JSON.stringify(resolutionSpec ?? null))
  const picked = dupMode(mode)
  const ruleCount = countPreferRules(resolution)

  const toggle = (name, on) => {
    const next = new Set(draft)
    if (on) next.add(name)
    else next.delete(name)
    setDraft(next)
  }

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Duplicates</h2>
        {active && <span className="badge on">on</span>}
      </header>

      {/* Five options, each with a paragraph, is a wall. Only the chosen one has
          to explain itself; the rest just have to be identifiable. */}
      <div className="mode-list">
        {DUP_MODES.map((m) => (
          <label key={m.id} className={`mode-option ${mode === m.id ? 'on' : ''}`} title={m.blurb}>
            <input type="radio" name="dup-mode" checked={mode === m.id} onChange={() => setMode(m.id)} />
            <span className="mode-text">
              <span className="mode-label">{m.label}</span>
              {mode === m.id && <span className="mode-blurb">{m.blurb}</span>}
            </span>
          </label>
        ))}
      </div>

      <p className="hint">
        {active
          ? `${dupMode(applied.mode).verb} ${applied.columns.join(' + ')}.`
          : 'Matched within the current filters.'}
        {resolves &&
          ruleCount > 0 &&
          (resolution?.merge
            ? ` ${ruleCount} rule${ruleCount === 1 ? '' : 's'} build the record.`
            : ` ${ruleCount} rule${ruleCount === 1 ? '' : 's'} pick the copy to keep.`)}
      </p>

      {columns.length > 10 && (
        <input
          className="input tiny"
          placeholder="Find column…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="value-options dup-columns">
        {visible.map((c) => (
          <label key={c.name} className="value-option">
            <input type="checkbox" checked={draft.has(c.name)} onChange={(e) => toggle(c.name, e.target.checked)} />
            <span className="value-text" title={c.name}>
              {c.name}
            </span>
          </label>
        ))}
        {visible.length === 0 && <span className="muted">No matching column.</span>}
      </div>

      {chosen.length > 1 && (
        <p className="footnote">Rows count as the same record only if all {chosen.length} match.</p>
      )}

      {resolves && (
        <ResolutionRules
          columns={columns}
          preferences={preferences}
          tiebreaks={tiebreaks}
          maxRules={maxResolveRules}
          resolution={resolution}
          setResolution={setResolution}
        />
      )}

      <div className="panel-actions">
        <button
          className="btn primary"
          // A mode that ignores the rules must not carry them: they would show up
          // in the status bar and in a saved view as though they had done something.
          onClick={() => onFind({ columns: chosen, mode, resolution: resolves ? resolutionSpec : null })}
          disabled={chosen.length === 0 || unchanged}
        >
          {scanning ? 'Scanning…' : active ? 'Update' : (picked.action ?? picked.label)}
        </button>
        {active && (
          <button className="btn subtle" onClick={onExit}>
            Show all rows
          </button>
        )}
      </div>
      {chosen.length === 0 && <p className="footnote">Pick at least one column.</p>}
    </section>
  )
}
